import React, { useEffect, useState } from "react";

import {
  bannerKey,
  clearLocalKlimVar,
  getLocalKlimVar,
  isPercentageComplete,
  setLocalKlimVar,
  useGetCarbonProgramByFarmId,
  useGetFootprintingCropAssessmentsRequiringReview,
  useGetFootprintingCropFootprints,
  useGetPracticeCrops,
  useMutateFootprintingCropCreateManyBlankPrimaryData,
  useTranslation,
} from "utils";

import { FOOTPRINTING_BANNER_KEYS } from "models";

import { AlertBanner, ErrorBoundary, Flex, Icon, IconButton, Modal, SelectableImageCard } from "components";

import { TaskOverviewDrawer } from "pages/app/farm/components/TaskOverviewDrawer";
import { AssessmentSelectionScreen } from "pages/app/footprinting/components/FootprintingAssessmentSectionScreen";
import FootprintingFormButton, { FootprintingLoadingState } from "pages/app/footprinting/components/FootprintingFormButton";

const ALERT_BANNER_CONFIGS = {
  FIRST_COMPLETE: {
    iconName: "tabler/circle-check",
    type: "#EBF2F0",
    titleKey: "footprintingAlertBanner.firstFootprintTitle",
    descriptionKey: "footprintingAlertBanner.firstFootprintDescription",
  },
  MULTIPLE_COMPLETE: {
    iconName: "tabler/info-circle",
    type: "#F4F4F4",
    titleKey: "footprintingAlertBanner.multipleFootprintTitle",
    descriptionKey: "footprintingAlertBanner.multipleFootprintDescription",
  },
  ALL_COMPLETE: {
    iconName: "tabler/circle-check",
    type: "#EBF2F0",
    titleKey: "footprintingAlertBanner.allAssessmentsCompletedTitle",
    descriptionKey: "footprintingAlertBanner.allAssessmentsCompletedDescription",
  },
  VERIFICATION: {
    iconName: "tabler/info-circle",
    type: "#FAFDDB",
    titleKey: "footprintingAlertBanner.awaitingVerificationTitle",
    descriptionKey: "footprintingAlertBanner.awaitingVerificationDescription",
    link: true,
    linkTextKey: "footprintingAlertBanner.awaitingVerificationLink",
    hasClick: true,
  },
  UNSUBMITTED_AFFECTED_BY_SPLIT: {
    iconName: "tabler/alert-triangle",
    type: "warning",
    titleKey: "assessmentManagement.unsubmittedAssessmentsAffectedBySplitBanner.header",
    descriptionKey: "assessmentManagement.unsubmittedAssessmentsAffectedBySplitBanner.description",
    buttonTextKey: "assessmentManagement.unsubmittedAssessmentsAffectedBySplitBanner.reviewButton",
    dismissButtonTextKey: "assessmentManagement.unsubmittedAssessmentsAffectedBySplitBanner.dismissButton",
  },
  SUBMITTED_AFFECTED_BY_SPLIT: {
    iconName: "tabler/info-circle",
    type: "#F4F4F4",
    titleKey: "assessmentManagement.submittedAssessmentsAffectedBySplitBanner.header",
    descriptionKey: "assessmentManagement.submittedAssessmentsAffectedBySplitBanner.description",
  },
} as const;

export const FootprintingYearPage = ({
  year,
  farmId,
  cropsWithFootprintForYear,
  yearIsSubmitted,
}: {
  year: number;
  farmId: string;
  cropsWithFootprintForYear: string[];
  yearIsSubmitted: boolean;
}) => {
  const { t } = useTranslation("footprinting");
  const [showTaskOverviewDrawer, setShowTaskOverviewDrawer] = useState(false);
  const [showAddAssessmentModal, setShowAddAssessmentModal] = useState(false);
  const [bannerConfig, setBannerConfig] = useState<(typeof ALERT_BANNER_CONFIGS)[keyof typeof ALERT_BANNER_CONFIGS] | null>(null);
  const [unsubmittedSplitBannerConfig, setUnsubmittedSplitBannerConfig] = useState<(typeof ALERT_BANNER_CONFIGS)["UNSUBMITTED_AFFECTED_BY_SPLIT"] | null>(null);
  const [submittedSplitBannerConfig, setSubmittedSplitBannerConfig] = useState<(typeof ALERT_BANNER_CONFIGS)["SUBMITTED_AFFECTED_BY_SPLIT"] | null>(null);
  const { data: allCrops } = useGetPracticeCrops({ enabled: true });
  const [cropAssessmentToReview, setCropAssessmentToReview] = useState<{ cropId: string; step: number } | null>(null);
  const carbonProgram = useGetCarbonProgramByFarmId(farmId);

  const cropProgressQueries = useGetFootprintingCropFootprints(farmId, [year], cropsWithFootprintForYear);
  // const dairyProgressQuery = useGetFootprintingDairyFootprint(farmId, year, { enabled: carbonProgram?.farmLevelFootprinting?.dairyEnabled });
  const { data: assessmentsRequiringReview, isLoading: isAssessmentsRequiringReviewLoading } = useGetFootprintingCropAssessmentsRequiringReview({
    enabled: true,
  });

  const allAssessmentsRequiringReviewForCurrentYear = Object.entries(assessmentsRequiringReview?.[year] ?? {});
  const submittedAssessmentsRequiringReviewForCurrentYear = allAssessmentsRequiringReviewForCurrentYear.filter(([_, assessment]) => assessment.isSubmitted);
  const unsubmittedAssessmentsRequiringReviewForCurrentYear = allAssessmentsRequiringReviewForCurrentYear.filter(([_, assessment]) => !assessment.isSubmitted);

  const [isMutating, setIsMutating] = useState(false);
  const [selectedCommodities, setSelectedCommodities] = useState<Record<string, boolean>>({});
  const { mutateAsync: createBlankPrimaryDataRows } = useMutateFootprintingCropCreateManyBlankPrimaryData(
    farmId,
    carbonProgram.data?.farmLevelFootprinting?.programCrops ?? [],
    year
  );

  useEffect(() => {
    const completedCropQueries = cropProgressQueries.filter((cpq) => isPercentageComplete(cpq.data?.completionPct ?? 0));
    const firstCompleteBannerKey = bannerKey(farmId, year, FOOTPRINTING_BANNER_KEYS.FIRST);
    const multipleCompleteBannerKey = bannerKey(farmId, year, FOOTPRINTING_BANNER_KEYS.MANY);
    const allCompleteBannerKey = bannerKey(farmId, year, FOOTPRINTING_BANNER_KEYS.ALL);
    const submittedAssessmentsRequiringReviewBannerKey = bannerKey(farmId, year, FOOTPRINTING_BANNER_KEYS.SUBMITTED_ASSESSMENTS);
    const unsubmittedAssessmentsRequiringReviewBannerKey = bannerKey(farmId, year, FOOTPRINTING_BANNER_KEYS.UNSUBMITTED_ASSESSMENTS);

    if (
      completedCropQueries.length >= 1 &&
      completedCropQueries.length === cropsWithFootprintForYear.length &&
      getLocalKlimVar(allCompleteBannerKey) === null
    ) {
      setLocalKlimVar(allCompleteBannerKey, "true");
      setBannerConfig(ALERT_BANNER_CONFIGS.ALL_COMPLETE);
    } else if (completedCropQueries.length === 1 && getLocalKlimVar(firstCompleteBannerKey) === null) {
      setLocalKlimVar(firstCompleteBannerKey, "true");
      setBannerConfig(ALERT_BANNER_CONFIGS.FIRST_COMPLETE);
    } else if (completedCropQueries.length > 1 && getLocalKlimVar(multipleCompleteBannerKey) === null) {
      setLocalKlimVar(multipleCompleteBannerKey, "true");
      setBannerConfig(ALERT_BANNER_CONFIGS.MULTIPLE_COMPLETE);
    }

    if (submittedAssessmentsRequiringReviewForCurrentYear.length && getLocalKlimVar(submittedAssessmentsRequiringReviewBannerKey) === null) {
      setLocalKlimVar(submittedAssessmentsRequiringReviewBannerKey, "true");
      setSubmittedSplitBannerConfig(ALERT_BANNER_CONFIGS.SUBMITTED_AFFECTED_BY_SPLIT);
    }

    if (unsubmittedAssessmentsRequiringReviewForCurrentYear.length) {
      setLocalKlimVar(unsubmittedAssessmentsRequiringReviewBannerKey, "true");
      setUnsubmittedSplitBannerConfig(ALERT_BANNER_CONFIGS.UNSUBMITTED_AFFECTED_BY_SPLIT);
    }
  }, [
    cropsWithFootprintForYear.length,
    cropProgressQueries,
    farmId,
    year,
    unsubmittedAssessmentsRequiringReviewForCurrentYear,
    submittedAssessmentsRequiringReviewForCurrentYear,
  ]);

  if (cropsWithFootprintForYear.length === 0) {
    const hasProgramCrops = carbonProgram.data?.farmLevelFootprinting?.programCrops && carbonProgram.data.farmLevelFootprinting.programCrops.length > 0;
    if (hasProgramCrops) {
      return <AssessmentSelectionScreen farmId={farmId} year={year} programCrops={carbonProgram.data?.farmLevelFootprinting?.programCrops} />;
    }
  }

  const addAssessments = () => {
    setIsMutating(true);
    createBlankPrimaryDataRows(
      Object.entries(selectedCommodities)
        .filter(([_, isSelected]) => isSelected)
        .map(([cropId]) => cropId)
    ).then(() => {
      clearLocalKlimVar(bannerKey(farmId, year, "all"));
      setIsMutating(false);
      setSelectedCommodities({});
      setShowAddAssessmentModal(false);
    });
  };

  const selectableCropIds = carbonProgram.data?.farmLevelFootprinting?.programCrops?.filter((cropId) => !cropsWithFootprintForYear.includes(cropId)) ?? [];

  const handleReviewCropAssessments = () => {
    const assessmentsRequiringReviewPerCurrentYear = Object.keys(assessmentsRequiringReview?.[year] ?? {});
    if (assessmentsRequiringReviewPerCurrentYear.length) {
      setCropAssessmentToReview({ cropId: assessmentsRequiringReviewPerCurrentYear[0], step: 1 });
    }
  };

  return (
    <div>
      {isMutating || cropProgressQueries.some((cpq) => cpq.isLoading) ? (
        <FootprintingLoadingState cardType="farm" />
      ) : (
        <FootprintingFormButton cardType="farm" year={year} farmId={farmId} yearIsSubmitted={yearIsSubmitted} />
      )}
      <Flex className="margin-top-200" align="center" justify="space-between">
        <h3 className="grey-600-text">{t("assessment")}</h3>
        {selectableCropIds.length > 0 ? <IconButton iconName="klim/plus" onClick={() => setShowAddAssessmentModal(true)} size="small" type="tertiary" /> : null}
      </Flex>
      <Flex>
        {!isAssessmentsRequiringReviewLoading && unsubmittedAssessmentsRequiringReviewForCurrentYear.length && unsubmittedSplitBannerConfig != null ? (
          <AlertBanner
            type={unsubmittedSplitBannerConfig.type}
            className="margin-top-100"
            iconName={unsubmittedSplitBannerConfig.iconName}
            iconSize={24}
            iconAlign="start"
            title={t(unsubmittedSplitBannerConfig.titleKey)}
            description={
              <Flex vertical gap={4}>
                <span className="paragraph-m">{t(unsubmittedSplitBannerConfig.descriptionKey)}</span>
                <Flex gap={32}>
                  <span className="paragraph-m-bold review-crop-assessments-button" onClick={handleReviewCropAssessments}>
                    {t(unsubmittedSplitBannerConfig.buttonTextKey)}
                  </span>
                </Flex>
              </Flex>
            }
          />
        ) : null}
        {!isAssessmentsRequiringReviewLoading && submittedAssessmentsRequiringReviewForCurrentYear.length && submittedSplitBannerConfig != null ? (
          <AlertBanner
            type={submittedSplitBannerConfig.type}
            className="margin-top-100"
            iconName={submittedSplitBannerConfig.iconName}
            iconSize={24}
            iconAlign="start"
            title={t(submittedSplitBannerConfig.titleKey)}
            description={t(submittedSplitBannerConfig.descriptionKey)}
            closeable
          />
        ) : null}
      </Flex>
      <Flex vertical className="margin-top-100">
        {bannerConfig !== null ? (
          <AlertBanner
            headingSize="h6"
            closeable
            iconSize={24}
            iconAlign="start"
            gap={4}
            iconName={bannerConfig.iconName}
            type={bannerConfig.type}
            title={t(bannerConfig.titleKey)}
            description={t(bannerConfig.descriptionKey)}
            onClose={() => setBannerConfig(null)}
            link={"link" in bannerConfig ? bannerConfig.link : undefined}
            linkText={"linkTextKey" in bannerConfig && bannerConfig.linkTextKey ? t(bannerConfig.linkTextKey) : undefined}
            onClick={"hasClick" in bannerConfig && bannerConfig.hasClick ? () => setShowTaskOverviewDrawer(true) : undefined}
          />
        ) : null}
      </Flex>

      {isMutating || cropProgressQueries.some((cpq) => cpq.isLoading) ? (
        <>
          <FootprintingLoadingState cardType="crop" />
        </>
      ) : (
        <>
          {cropsWithFootprintForYear.map((cropId: string) => (
            <FootprintingFormButton
              key={`${year}-${cropId}`}
              cardType="crop"
              farmId={farmId}
              year={year}
              cropId={cropId}
              yearIsSubmitted={yearIsSubmitted}
              initialStep={cropAssessmentToReview?.cropId === cropId ? cropAssessmentToReview.step : undefined}
              onFormFlowClose={() => setCropAssessmentToReview(null)}
            />
          ))}
        </>
      )}
      {carbonProgram.data?.platformFeatures?.footprinting?.dairy === true ? (
        <FootprintingFormButton cardType="dairy" farmId={farmId} year={year} yearIsSubmitted={yearIsSubmitted} />
      ) : null}
      <ErrorBoundary>
        <TaskOverviewDrawer
          farmTasks={[]}
          visible={showTaskOverviewDrawer}
          onClose={() => setShowTaskOverviewDrawer(false)}
          onDataChange={() => {}}
          loading={false}
        />
        <Modal
          maskClosable
          showModal={showAddAssessmentModal}
          onCancel={() => setShowAddAssessmentModal(false)}
          onOk={addAssessments}
          okText={t("assessmentManagement.addAssessmentModal.button")}
          okButtonProps={{ disabled: Object.values(selectedCommodities).filter(Boolean).length === 0 }}
          cancelText={t("modal.cancel")}
          title={t("assessmentManagement.addAssessmentModal.title")}
          content={
            selectableCropIds.length > 0 ? (
              selectableCropIds.map((cropId) => {
                const cropInfo = allCrops?.find((c) => c.id === cropId);
                if (!cropInfo) return null;

                return (
                  <SelectableImageCard
                    key={cropInfo.id}
                    selectionMode="multiple"
                    title={cropInfo.name}
                    checked={selectedCommodities[cropId]}
                    onChange={() => setSelectedCommodities({ ...selectedCommodities, [cropId]: !selectedCommodities[cropId] })}
                    image={{
                      imageAlt: `${cropInfo.name} image`,
                      imageSrc: cropInfo.imageURL,
                    }}
                  />
                );
              })
            ) : (
              <Flex vertical className="padding-200 " gap={24} align="center">
                <Icon className="grey-text" iconName="klim/crops-matured" size="xl" />
                <p className="grey-text center">{t("assessmentManagement.addAssessmentModal.noneAvailable")}</p>
              </Flex>
            )
          }
        />
      </ErrorBoundary>
    </div>
  );
};
